import { useEffect } from 'react';

const WOWHEAD_SCRIPT_URL = 'https://wow.zamimg.com/widgets/power.js';

let scriptLoaded = false;
let scriptLoading = false;
let scriptFailed = false;

function loadWowheadScript() {
  if (scriptLoaded || scriptLoading) {
    return;
  }
  scriptLoading = true;

  (window as any).wowhead_tooltips = {
    colorlinks: false,
    iconizelinks: false,
    renamelinks: false,
    iconSize: 'small'
  };

  const script = document.createElement('script');
  script.src = WOWHEAD_SCRIPT_URL;
  script.async = true;
  script.onload = () => {
    scriptLoaded = true;
  };
  script.onerror = () => {
    scriptFailed = true;
    console.error('Failed to load Wowhead tooltip script');
  };
  document.body.appendChild(script);
}

function isWowheadLink(target: EventTarget | null): Element | null {
  if (!target || !(target instanceof HTMLElement)) {
    return null;
  }
  return target.closest('a[href*="wowhead.com"]');
}

function isTouchDevice(): boolean {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

export function WowheadLoader() {
  useEffect(() => {
    if (scriptLoaded) {
      return;
    }

    // On touch devices, load script immediately (no hover events)
    if (isTouchDevice()) {
      loadWowheadScript();
      return;
    }

    // Track mouse position so we can dispatch properly-positioned synthetic events
    let lastMouseX = 0;
    let lastMouseY = 0;
    const trackMouse = (e: MouseEvent) => {
      lastMouseX = e.clientX;
      lastMouseY = e.clientY;
    };
    document.addEventListener('mousemove', trackMouse);

    // On desktop, load on first hover over a Wowhead link
    let pendingLink: Element | null = null;

    const removeListeners = () => {
      document.removeEventListener('mousemove', trackMouse);
      document.removeEventListener('mouseover', handleWowheadHover);
      document.removeEventListener('focusin', handleWowheadHover);
    };

    const waitForScriptAndTrigger = () => {
      if (scriptFailed) {
        pendingLink = null;
        removeListeners();
        return;
      }
      if (scriptLoaded) {
        if (pendingLink) {
          // Only re-trigger if the mouse is still over the pending link
          const elementUnderMouse = document.elementFromPoint(lastMouseX, lastMouseY);
          if (elementUnderMouse && pendingLink.contains(elementUnderMouse)) {
            pendingLink.dispatchEvent(new MouseEvent('mouseover', {
              bubbles: true,
              clientX: lastMouseX,
              clientY: lastMouseY
            }));
          }
          pendingLink = null;
        }
        removeListeners();
        return;
      }
      requestAnimationFrame(waitForScriptAndTrigger);
    };

    function handleWowheadHover(e: Event) {
      if (scriptLoaded) {
        removeListeners();
        return;
      }

      const link = isWowheadLink(e.target);
      if (!link) {
        return;
      }

      const wasNotLoading = !scriptLoading;
      if (wasNotLoading) {
        pendingLink = link;
      }
      loadWowheadScript();
      if (wasNotLoading) {
        requestAnimationFrame(waitForScriptAndTrigger);
      }
    }

    document.addEventListener('mouseover', handleWowheadHover);
    document.addEventListener('focusin', handleWowheadHover);

    return removeListeners;
  }, []);

  return null;
}
